import { land, water } from '../config/continents';

const CELL_X = 15;
const CELL_Y = 16;
const FONT_SIZE = 11;
const FONT = `${FONT_SIZE}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;
const COLOR = [150, 160, 222];
const FLICKER_MS = 120;

// Pointer light, same feel as the ALIA login (ia/web BinaryBackground): digits near the
// cursor light up to white at once and fade back over FADE_MS once it moves away.
// Only the continents light up; the background around them stays dark.
const LIGHT_RADIUS = 150;
const LIGHT_JITTER = 50; // the noise field bends the radius so the light has ragged edges
const LIGHT_CORE = 1.8; // > 1 widens the fully lit centre
const FADE_MS = 600;

// World map framing: the Atlantic sits in the middle, the Americas on the left
// and Europe/Africa on the right, as in the brand artwork.
const MAP_CENTER_LON = -40;
const MAP_CENTER_LAT = 18;
const MAP_LAT_SPAN = 165;

const rand = Math.random;
const lerp = (a, b, t) => a + (b - a) * t;

/**
 * Rasterizes the continents into a cols×rows grid and returns how much of each cell is land (0–1).
 */
function landMask(cols, rows) {
  const canvas = document.createElement('canvas');
  canvas.width = cols;
  canvas.height = rows;

  const ctx = canvas.getContext('2d');
  const deg = Math.min(360 / cols, MAP_LAT_SPAN / rows);
  const cx = cols / 2;
  const cy = rows / 2;

  const fill = polygon => [-360, 0, 360].forEach(shift => {
    ctx.beginPath();
    for (let i = 0; i < polygon.length; i += 2) {
      const x = cx + (polygon[i] + shift - MAP_CENTER_LON) / deg;
      const y = cy - (polygon[i + 1] - MAP_CENTER_LAT) / deg;
      if (i) ctx.lineTo(x, y);
      else ctx.moveTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  });

  ctx.fillStyle = '#fff';
  land.forEach(fill);
  ctx.globalCompositeOperation = 'destination-out';
  water.forEach(fill);

  const { data } = ctx.getImageData(0, 0, cols, rows);
  const mask = new Float32Array(cols * rows);
  for (let i = 0; i < mask.length; i++) mask[i] = data[i * 4 + 3] / 255;

  return mask;
}

/**
 * Draws a world map of 0/1 digits on a canvas, makes a few of them flicker and lights
 * up the digits around the mouse pointer.
 *
 * @param {HTMLCanvasElement} canvas Canvas sized by CSS to cover the viewport
 * @param {{ animate?: boolean }} options `false` draws a still map (reduced motion)
 */
export default function createMatrix(canvas, { animate = true } = {}) {
  const ctx = canvas && canvas.getContext && canvas.getContext('2d');
  if (!ctx) return { resize() {}, destroy() {} };

  let cells = [];
  let lit = [];
  let cols = 0;
  let rows = 0;
  let width = 0;
  let height = 0;
  let timer = null;

  let pointer = null;
  let glowing = new Set();
  let raf = 0;
  let lastFrame = 0;

  const drawCell = cell => {
    ctx.clearRect(cell.x - 1, cell.y - CELL_Y + 3, CELL_X, CELL_Y);

    const alpha = Math.min(1, cell.alpha + cell.glow);
    if (!alpha) return;

    const r = Math.round(lerp(COLOR[0], 255, cell.glow));
    const g = Math.round(lerp(COLOR[1], 255, cell.glow));
    const b = Math.round(lerp(COLOR[2], 255, cell.glow));
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
    ctx.fillText(cell.char, cell.x, cell.y);
  };

  const build = () => {
    const rect = canvas.getBoundingClientRect();
    width = rect.width;
    height = rect.height;
    if (!width || !height) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = FONT;
    ctx.textBaseline = 'alphabetic';

    cols = Math.ceil(width / CELL_X) + 1;
    rows = Math.ceil(height / CELL_Y) + 1;
    const mask = landMask(cols, rows);

    cells = [];
    lit = [];
    glowing = new Set();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const isLit = mask[r * cols + c] > 0.45;
        const cell = {
          x: c * CELL_X + 2,
          y: r * CELL_Y + FONT_SIZE + 1,
          char: rand() < 0.5 ? '0' : '1',
          alpha: isLit
            ? 0.2 + rand() * 0.3 + (rand() < 0.06 ? 0.3 : 0)
            : (rand() < 0.5 ? 0.04 + rand() * 0.05 : 0),
          glow: 0,
          land: isLit,
          noise: (Math.sin(c * 0.15) + Math.cos(r * 0.15) + Math.sin((c + r) * 0.08)) / 3,
        };
        cells.push(cell);
        if (isLit) lit.push(cell);
      }
    }

    ctx.clearRect(0, 0, width, height);
    cells.forEach(drawCell);
  };

  const flicker = () => {
    if (!document.hidden && lit.length) {
      const count = Math.max(4, Math.round(lit.length * 0.012));
      for (let i = 0; i < count; i++) {
        const cell = lit[Math.floor(rand() * lit.length)];
        cell.char = cell.char === '0' ? '1' : '0';
        cell.alpha = Math.min(0.85, Math.max(0.14, cell.alpha + (rand() - 0.5) * 0.3));
        drawCell(cell);
      }
    }
    timer = setTimeout(flicker, FLICKER_MS);
  };

  // Only the cells around the pointer and the ones still fading are repainted, and
  // the loop stops as soon as nothing changes.
  const shine = now => {
    const fade = lastFrame ? (now - lastFrame) / FADE_MS : 0;
    lastFrame = now;
    let changed = false;

    const touched = new Set();
    if (pointer) {
      const reach = LIGHT_RADIUS + LIGHT_JITTER;
      const c0 = Math.max(0, Math.floor((pointer.x - reach) / CELL_X));
      const c1 = Math.min(cols - 1, Math.ceil((pointer.x + reach) / CELL_X));
      const r0 = Math.max(0, Math.floor((pointer.y - reach) / CELL_Y));
      const r1 = Math.min(rows - 1, Math.ceil((pointer.y + reach) / CELL_Y));

      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          const cell = cells[r * cols + c];
          if (!cell.land) continue;

          const radius = LIGHT_RADIUS + cell.noise * LIGHT_JITTER;
          const dist = Math.hypot(cell.x + 3 - pointer.x, cell.y - 4 - pointer.y);
          if (dist >= radius) continue;

          const target = Math.min(1, (1 - dist / radius) * LIGHT_CORE);
          touched.add(cell);
          if (target > cell.glow) {
            cell.glow = target;
            glowing.add(cell);
            drawCell(cell);
            changed = true;
          }
        }
      }
    }

    glowing.forEach(cell => {
      if (touched.has(cell)) return;
      cell.glow = Math.max(0, cell.glow - fade);
      if (!cell.glow) glowing.delete(cell);
      drawCell(cell);
      changed = true;
    });

    if (changed) raf = requestAnimationFrame(shine);
    else {
      raf = 0;
      lastFrame = 0;
    }
  };

  const wake = () => {
    if (!raf) raf = requestAnimationFrame(shine);
  };

  const onMouseMove = event => {
    const rect = canvas.getBoundingClientRect();
    pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    wake();
  };

  const onMouseLeave = event => {
    if (event.relatedTarget) return;
    pointer = null;
    wake();
  };

  build();

  if (animate) {
    timer = setTimeout(flicker, FLICKER_MS);
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('mouseout', onMouseLeave);
  }

  return {
    resize: build,
    destroy() {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseout', onMouseLeave);
    },
  };
}
